import { FreightCalculator, freightCalculator } from './FreightCalculator'
import type { FreightMode, OptimizationItem, SupplierFreightRule } from '../types'

export interface CostLineInput {
  demandId: string
  productId: string
  product: string
  sku: string
  supplier: string
  quantity: number
  unitPrice: number
  delivery: number
}

export interface CostBreakdown {
  productCost: number
  freightCost: number
  discountTotal: number
  feeTotal: number
  totalCost: number
  items: OptimizationItem[]
  freightBySupplier: Array<{
    supplier: string
    productCost: number
    freight: number
    freeFreightApplied: boolean
    mode: FreightMode
    deliveryDays: number
  }>
  warnings: string[]
}

/**
 * CostCalculator — products + freight + discounts + free freight + fees.
 */
export class CostCalculator {
  private freight: FreightCalculator

  constructor(freight: FreightCalculator = freightCalculator) {
    this.freight = freight
  }

  setFreightRules(rules: SupplierFreightRule[]) {
    this.freight.setRules(rules)
  }

  calculate(
    lines: CostLineInput[],
    options: { discountRate?: number; feeRate?: number } = {},
  ): CostBreakdown {
    const discountRate = options.discountRate ?? 0
    const feeRate = options.feeRate ?? 0
    const warnings: string[] = []

    const items: OptimizationItem[] = lines.map((line) => ({
      product: line.product,
      productId: line.productId,
      demandId: line.demandId,
      supplier: line.supplier,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      subtotal: round2(line.unitPrice * line.quantity),
      delivery: line.delivery,
      sku: line.sku,
    }))

    const bySupplier = new Map<string, OptimizationItem[]>()
    for (const item of items) {
      const list = bySupplier.get(item.supplier) ?? []
      list.push(item)
      bySupplier.set(item.supplier, list)
    }

    let productCost = 0
    let freightCost = 0
    const freightBySupplier: CostBreakdown['freightBySupplier'] = []

    for (const [supplier, supplierItems] of bySupplier) {
      const supplierProductCost = round2(supplierItems.reduce((s, i) => s + i.subtotal, 0))
      productCost += supplierProductCost

      const freight = this.freight.calculate({
        supplier,
        productSubtotal: supplierProductCost,
      })

      if (!freight.minOrderMet) {
        warnings.push(
          `${supplier}: valor mínimo do pedido não atingido (falta ${freight.shortfall.toFixed(2)})`,
        )
      }

      freightCost += freight.amount
      freightBySupplier.push({
        supplier,
        productCost: supplierProductCost,
        freight: freight.amount,
        freeFreightApplied: freight.freeFreightApplied,
        mode: freight.mode,
        deliveryDays: Math.max(...supplierItems.map((i) => i.delivery)),
      })
    }

    productCost = round2(productCost)
    freightCost = round2(freightCost)
    const discountTotal = round2(productCost * discountRate)
    const feeTotal = round2((productCost - discountTotal + freightCost) * feeRate)
    const totalCost = round2(productCost - discountTotal + freightCost + feeTotal)

    return {
      productCost,
      freightCost,
      discountTotal,
      feeTotal,
      totalCost,
      items,
      freightBySupplier: freightBySupplier.sort((a, b) => a.supplier.localeCompare(b.supplier)),
      warnings,
    }
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const costCalculator = new CostCalculator()
